import { env } from '@/lib/env';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@iq/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageViewer } from './image-viewer';
import { sourceDisplayName } from './source-display-name';
import { SourceMarkdownPreview } from './source-markdown-preview';

export interface Citation {
  index: number;
  docId: string;
  docName?: string;
  kbId?: string;
  imageRef?: string;
  chunk?: string;
}

interface SourcePreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  content?: string;
  contentType?: string;
  filename?: string;
}

export interface CitationCardProps {
  citation: Citation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CitationCard({ citation, open, onOpenChange }: CitationCardProps) {
  const { t } = useTranslation();
  const [fullscreen, setFullscreen] = useState(false);
  const [sourcePreview, setSourcePreview] = useState<SourcePreviewState>({ status: 'idle' });

  useEffect(() => {
    if (!open) setFullscreen(false);
  }, [open]);

  useEffect(() => {
    if (!open || !citation?.kbId || !citation.docId || citation.imageRef) {
      setSourcePreview({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setSourcePreview({ status: 'loading' });
    const url = `${env.VITE_API_BASE_URL}/sources/${encodeURIComponent(
      citation.kbId,
    )}/${encodeURIComponent(citation.docId)}`;
    fetch(url, { credentials: 'include', signal: ctrl.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`source preview failed: ${res.status}`);
        return (await res.json()) as {
          content?: unknown;
          contentType?: unknown;
          filename?: unknown;
        };
      })
      .then((body) => {
        if (typeof body.content !== 'string' || body.content.trim().length === 0) {
          throw new Error('source preview was empty');
        }
        const nextPreview: SourcePreviewState = {
          status: 'ready',
          content: body.content,
        };
        if (typeof body.contentType === 'string') nextPreview.contentType = body.contentType;
        if (typeof body.filename === 'string') nextPreview.filename = body.filename;
        setSourcePreview(nextPreview);
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') setSourcePreview({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [open, citation?.kbId, citation?.docId, citation?.imageRef]);

  if (!citation) return null;
  const hasImage = Boolean(citation.imageRef);
  const previewText = sourcePreview.content ?? citation.chunk;
  const hasChunk = Boolean(previewText);
  const rawTitle = sourcePreview.filename ?? citation.docName;
  const title = sourceDisplayName(rawTitle, previewText) ?? t('chat.citation.defaultTitle');
  const showMarkdown =
    sourcePreview.status === 'ready' && isMarkdownSource(sourcePreview.contentType, title);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="citation-card"
        className={cn(
          'grid gap-0 overflow-hidden p-0',
          fullscreen
            ? 'inset-0 left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0'
            : [
                // Mobile (<640px): hug the viewport (small inset only) so
                // the image gets every available pixel. Floating card on
                // ≥sm.
                'h-[calc(100dvh-1rem)] w-[calc(100vw-0.5rem)] max-w-none rounded-xl',
                'sm:h-[min(720px,calc(100dvh-2rem))] sm:w-[calc(100vw-1rem)] sm:max-w-3xl sm:rounded-2xl',
              ],
          // The image viewer + chunk-text footer live in a flex track so
          // the footer's auto height never starves the image and the
          // image always fills the remaining viewport. Grid rows:
          //   row 1 — header (auto)
          //   row 2 — viewer + chunk strip (minmax(0,1fr))
          'grid-rows-[auto_minmax(0,1fr)]',
        )}
      >
        <DialogHeader className="min-w-0 space-y-0.5 border-b px-4 py-3 pr-12 text-left sm:space-y-1 sm:px-5 sm:py-4 sm:text-left">
          {/* `min-w-0` on the header (a grid item of the DialogContent grid)
              lets the title shrink so `truncate` can ellipsize a long
              filename instead of overflowing under the close button. The
              tooltip surfaces the full name on hover/focus. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTitle className="min-w-0 cursor-default truncate text-base sm:text-lg">
                {title}
              </DialogTitle>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="start"
              className="max-w-[80vw] break-words sm:max-w-md"
            >
              {title}
            </TooltipContent>
          </Tooltip>
          <DialogDescription className="text-xs sm:text-sm">
            {t('chat.citation.source', { index: citation.index })}
          </DialogDescription>
        </DialogHeader>
        {hasImage ? (
          // Image + (optional) chunk strip share a column. The chunk strip
          // is capped at a small fraction of the viewport so the image
          // never gets squeezed out by long quotes.
          <div
            className={cn(
              'grid h-full w-full min-h-0',
              hasChunk ? 'grid-rows-[minmax(0,1fr)_auto]' : 'grid-rows-[minmax(0,1fr)]',
            )}
          >
            <ImageViewer
              docId={citation.docId}
              imageRef={citation.imageRef as string}
              alt={t('chat.citation.imageAlt')}
              fullscreen={fullscreen}
              onToggleFullscreen={() => setFullscreen((f) => !f)}
            />
            {hasChunk ? (
              <div className="border-t bg-muted/30">
                <ScrollArea
                  type="always"
                  className="max-h-[18dvh] sm:max-h-[22dvh] lg:max-h-[24vh]"
                >
                  <p className="whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed text-foreground sm:px-5">
                    {previewText}
                  </p>
                </ScrollArea>
              </div>
            ) : null}
          </div>
        ) : hasChunk ? (
          <ScrollArea type="always" className="h-full">
            <div className="space-y-2 px-4 py-4 sm:px-5">
              {sourcePreview.status === 'loading' ? (
                <p className="text-xs text-muted-foreground">
                  {t('chat.citation.loadingSource', { defaultValue: 'Loading full source...' })}
                </p>
              ) : null}
              {sourcePreview.status === 'ready' ? (
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('chat.citation.fullSource', { defaultValue: 'Full source' })}
                </p>
              ) : null}
              {sourcePreview.status === 'error' ? (
                <p className="text-xs text-muted-foreground">
                  {t('chat.citation.chunkFallback', {
                    defaultValue: 'Full source unavailable. Showing cited chunk.',
                  })}
                </p>
              ) : null}
              {showMarkdown ? (
                <div className="rounded-2xl border bg-background/80 px-4 py-4 text-sm shadow-sm sm:px-5 sm:py-5">
                  <SourceMarkdownPreview content={previewText ?? ''} />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground">
                  {previewText}
                </pre>
              )}
            </div>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function isMarkdownSource(contentType: string | undefined, filename: string) {
  const normalizedType = contentType?.toLowerCase().split(';', 1)[0]?.trim();
  if (normalizedType === 'text/markdown' || normalizedType === 'text/x-markdown') return true;

  const normalizedName = filename.toLowerCase();
  return normalizedName.endsWith('.md') || normalizedName.endsWith('.markdown');
}
